import {
  Element as PresentationElement,
  GetAvailableRegionsResponse,
  ListallViewsinaprojectResponse,
  ViewDetailsResponse,
} from '@data-access/connect/tc-tcps';
import { convertConnectCameraToViewerCamera } from './camera-conversion';
import { createSignal } from 'solid-js';
import { SharedCapabilityProviderBase } from '../base/shared-provider-base';
import { PositioningData } from '../models/positioning-data';
import { MeasurementsCapabilityProvider } from '../measurements/measurements-provider';
import { MarkupsCapabilityProvider } from '../markups/markups-provider';
import {
  convertScan3dStyleToPointCloudStyling,
  isMeasurement,
} from './views-helpers';
import { Scan3dStyle } from '@trimble/types';
import { PointCloudStylingCapabilityProvider } from '../point-cloud/point-cloud-styling-provider';
import { LoadViewOptions, ViewWithClipping } from './views-provider.types';

export const MODEL_TRANSPARENCY_MIN_PERCENT = 20;
export const TRANSPARENCY_MAX_PERCENT = 100;
export const FULLY_OPAQUE_STATION_OPACITY_FRACTION = 0.999;

interface ScanDataApiService {
  getSetting(
    region: GetAvailableRegionsResponse,
    projectId: string,
    key: string
  ): Promise<{
    data?: { scans?: Array<{ id: string; scan3dStyle?: Scan3dStyle }> };
  } | null>;

  savePointCloudsForView(
    region: GetAvailableRegionsResponse,
    projectId: string,
    viewId: string,
    visiblePointCloudIds: string[],
    scan3dStyle?: Scan3dStyle
  ): Promise<void>;
}

export class ViewsCapabilityProvider extends SharedCapabilityProviderBase<ViewDetailsResponse> {
  public idKey = 'id' as const;

  private readonly positionData: () => PositioningData | null;
  private readonly setPositionData: (value: PositioningData | null) => void;

  private readonly presentationElements: () => PresentationElement[] | null;
  private readonly setPresentationElements: (
    value: PresentationElement[] | null
  ) => void;

  private readonly globalModelTransparencyLevel: () => number | null;
  private readonly setGlobalModelTransparencyLevel: (
    value: number | null
  ) => void;

  private readonly globalPointCloudTransparencyLevel: () => number | null;
  private readonly setGlobalPointCloudTransparencyLevel: (
    value: number | null
  ) => void;

  private readonly globalStationTransparencyLevel: () => number | null;
  private readonly setGlobalStationTransparencyLevel: (
    value: number | null
  ) => void;

  constructor(
    views: ListallViewsinaprojectResponse,
    private readonly measurementsProvider: MeasurementsCapabilityProvider,
    private readonly markupsProvider: MarkupsCapabilityProvider,
    private readonly pointCloudStylingProvider: PointCloudStylingCapabilityProvider,
    private readonly scanDataApi: ScanDataApiService,
    private readonly loadExclusiveModels: (ids: string[]) => void,
    private readonly loadExclusivePointClouds: (ids: string[]) => void
  ) {
    super();
    const [positionData, setPositionData] =
      createSignal<PositioningData | null>(null);
    this.positionData = positionData;
    this.setPositionData = setPositionData;

    const [presentationElements, setPresentationElements] = createSignal<
      PresentationElement[] | null
    >(null);
    this.presentationElements = presentationElements;
    this.setPresentationElements = setPresentationElements;

    const [globalModelTransparencyLevel, setGlobalModelTransparencyLevel] =
      createSignal<number | null>(null);
    this.globalModelTransparencyLevel = globalModelTransparencyLevel;
    this.setGlobalModelTransparencyLevel = setGlobalModelTransparencyLevel;

    const [
      globalPointCloudTransparencyLevel,
      setGlobalPointCloudTransparencyLevel,
    ] = createSignal<number | null>(null);
    this.globalPointCloudTransparencyLevel = globalPointCloudTransparencyLevel;
    this.setGlobalPointCloudTransparencyLevel =
      setGlobalPointCloudTransparencyLevel;

    const [globalStationTransparencyLevel, setGlobalStationTransparencyLevel] =
      createSignal<number | null>(null);
    this.globalStationTransparencyLevel = globalStationTransparencyLevel;
    this.setGlobalStationTransparencyLevel = setGlobalStationTransparencyLevel;

    this.setItems(views);
  }

  private updatePosition(data: PositioningData): void {
    this.setPositionData(data);
  }

  public updateViews(views: ListallViewsinaprojectResponse): void {
    this.setItems(views);
  }

  public getPositionData(): PositioningData | null {
    return this.positionData();
  }

  public getPresentationElements(): PresentationElement[] | null {
    return this.presentationElements();
  }

  public getGlobalModelTransparencyLevel(): number | null {
    return this.globalModelTransparencyLevel();
  }

  /** User-facing setter; clamps to 20–100 or clears with null (fully opaque). */
  public updateGlobalModelTransparencyLevel(level: number | null): void {
    if (level === null) {
      this.setGlobalModelTransparencyLevel(null);
      return;
    }
    const clamped = Math.max(
      MODEL_TRANSPARENCY_MIN_PERCENT,
      Math.min(TRANSPARENCY_MAX_PERCENT, Math.round(level))
    );
    this.setGlobalModelTransparencyLevel(clamped);
  }

  public getGlobalPointCloudTransparencyLevel(): number | null {
    return this.globalPointCloudTransparencyLevel();
  }

  /** Clamps to 0–100 or clears with null (fully opaque). */
  public updateGlobalPointCloudTransparencyLevel(level: number | null): void {
    if (level === null) {
      this.setGlobalPointCloudTransparencyLevel(null);
      return;
    }
    const clamped = Math.max(
      0,
      Math.min(TRANSPARENCY_MAX_PERCENT, Math.round(level))
    );
    this.setGlobalPointCloudTransparencyLevel(clamped);
  }

  public getGlobalStationTransparencyLevel(): number | null {
    return this.globalStationTransparencyLevel();
  }

  /** Clamps to 0–100 or clears with null (fully opaque). */
  public updateGlobalStationTransparencyLevel(level: number | null): void {
    if (level === null) {
      this.setGlobalStationTransparencyLevel(null);
      return;
    }
    const clamped = Math.max(
      0,
      Math.min(TRANSPARENCY_MAX_PERCENT, Math.round(level))
    );
    this.setGlobalStationTransparencyLevel(clamped);
  }

  /**
   * Opacity for the current station's panorama model. Falls back to 0.999
   * when fully opaque to keep `transparent` stable in three.js and avoid a
   * shader recompile (see `image-capability-base.ts`).
   */
  public getStationOpacityFraction(): number {
    const level = this.globalStationTransparencyLevel();
    if (level == null || level >= TRANSPARENCY_MAX_PERCENT) {
      return FULLY_OPAQUE_STATION_OPACITY_FRACTION;
    }
    return level / TRANSPARENCY_MAX_PERCENT;
  }

  public resetAllGlobalTransparencyLevels(): void {
    this.setGlobalModelTransparencyLevel(null);
    this.setGlobalPointCloudTransparencyLevel(null);
    this.setGlobalStationTransparencyLevel(null);
  }

  public async loadView(
    view: ViewWithClipping,
    region: GetAvailableRegionsResponse,
    options?: LoadViewOptions
  ): Promise<void> {
    this.handleModels(view);
    await this.handlePointClouds(view, region);
    this.handleMarkups(view);
    if (!options?.skipCamera) {
      this.handleCamera(view);
    }
    this.setPresentationElements(view.presentation?.elements ?? null);
    this.setGlobalModelTransparencyLevel(
      view.presentation?.transparencyLevel ?? null
    );
  }

  private handleModels(view: ViewDetailsResponse): void {
    const modelIds = (view.files || []).filter((id) => id != null && id !== '');
    this.loadExclusiveModels(modelIds);
  }

  private handleCamera(view: ViewDetailsResponse): void {
    const { position, quaternion, fieldOfView } =
      convertConnectCameraToViewerCamera(view.camera, false);

    if (!position || !quaternion) {
      console.error('Cannot set camera position: missing data');
      return;
    }

    const positioningData: PositioningData = {
      cameraPosition: position,
      targetPosition: null,
      quaternion: [quaternion.x, quaternion.y, quaternion.z, quaternion.w],
      fov: fieldOfView ?? null,
    };

    this.updatePosition(positioningData);
  }

  private handleMarkups(view: ViewDetailsResponse): void {
    // Clear existing markups/measurements when loading a new view
    this.measurementsProvider.removeAllMeasurements();
    this.markupsProvider.removeAllMarkups();

    if (!view.markups || view.markups.length === 0) {
      return;
    }

    const measurements = view.markups.filter(isMeasurement);
    const markups = view.markups.filter((markup) => !isMeasurement(markup));

    this.measurementsProvider.loadMeasurementsForConnectView(measurements);

    this.markupsProvider.loadMarkupsForConnectView(markups);
  }

  private async handlePointClouds(
    view: ViewDetailsResponse,
    region: GetAvailableRegionsResponse
  ): Promise<void> {
    if (!view.projectId) {
      return;
    }

    try {
      // Get scanIds associated with the view (null = no setting found / 404)
      const setting = await this.scanDataApi.getSetting(
        region,
        view.projectId,
        `view_${view.id}`
      );

      const scans = setting?.data?.scans;

      // For views with no scans, unload all pointclouds
      if (!scans || scans.length === 0) {
        this.loadExclusivePointClouds([]);
        return;
      }

      // Note: only one global style is supported, first style is used
      const scanWithStyle = scans.find(
        (scan) => scan.scan3dStyle !== undefined
      );

      if (scanWithStyle?.scan3dStyle) {
        const styling = convertScan3dStyleToPointCloudStyling(
          scanWithStyle.scan3dStyle
        );

        // Saved view restores full style; discard session overrides
        this.pointCloudStylingProvider.clearUserClassOverrides();
        this.pointCloudStylingProvider.setPointCloudStyling(styling);
      }

      const scanIds = scans
        .map((scan) => scan.id)
        .filter((id): id is string => typeof id === 'string' && id !== '');

      // Load point clouds - they will automatically get the styling applied
      this.loadExclusivePointClouds(scanIds);
    } catch (error) {
      console.error('Failed to load pointclouds from view:', error);
    }
  }

  public cleanup(): Promise<void> {
    this.setPositionData(null);
    this.setPresentationElements(null);
    this.resetAllGlobalTransparencyLevels();
    this.loadExclusiveModels([]);
    this.loadExclusivePointClouds([]);
    this.markupsProvider.removeAllMarkups();
    this.measurementsProvider.removeAllMeasurements();

    return Promise.resolve();
  }
}
